'use client';

/**
 * Shared `back(amount)` tx-engine helper.
 *
 * The post-R6 contract removed the `proposal_stake` instruction; backers
 * now call the universal `back` instruction directly on a market in the
 * `Backing` lifecycle phase (see `contracts/markets/programs/markets/src/
 * instructions/backing/back.rs`). Three call sites share this helper:
 *
 *   1. `components/launchpad/back-proposal-modal.tsx` — the standalone
 *      "Back this market" modal opened from the launchpad detail page.
 *   2. `components/launchpad/proposal-form.tsx` — the inline panel-style
 *      back form on the launchpad detail page.
 *   3. `components/launchpad/create-proposal-dialog.tsx` — the optional
 *      back step fired immediately after a successful `useCreateMarket`
 *      mutation when the user opted in to a founding stake.
 *
 * Keeping the call shape in one place ensures every back path uses the
 * same compute-budget defaults, ATA prep, and submit-tx contract.
 */

import {
  type ActiveWallet,
  createTransactionTraceId,
  isAmbiguousLandingError,
  reconcileBackerRewardAfterBacking,
  type SigningDeps,
  type SubmissionResult,
  TxErrorCode,
} from '@morfi/backend-sdk-v2/client';
import type { Connection, PublicKey as PublicKeyType } from '@solana/web3.js';

import { onEvent } from '@/events';
import type { Network } from '@/lib/network';
import { logger } from '@/lib/telemetry/logger';
import type { useSubmitTransaction } from '@/lib/tx-engine/use-submit-transaction';

type SubmitTxFn = ReturnType<typeof useSubmitTransaction>;
type TxError = Extract<SubmissionResult, { ok: false }>['error'];

interface BackTxErrorContext {
  readonly traceId: string;
  readonly activeWallet: Pick<ActiveWallet, 'address' | 'type'>;
  readonly marketPubkey: string;
  readonly outcomeIndex: number;
  readonly network: Network;
  /** `Date.now()` at the start of the attempt; yields `latencyMs`. */
  readonly startedAt: number;
}

interface BackTxErrorFields {
  readonly errorCode: string;
  readonly stage: string;
  readonly morfiError: string;
  readonly recoveryAction: string;
  readonly errorClass: string;
}

/**
 * Statsig outcome for a failed back/unback — the same `trade_error` event
 * and shape the SDK trading hooks emit for buy/sell/swap/claim/boost, so the
 * trade funnel counts every on-chain action's failure under one name with
 * `instruction` as the dimension. Mirrors the SDK remap too: once the tx was
 * submitted, a code that leaves the on-chain landing unknown is
 * `trade_unknown`, not a failure. Cancelled attempts stay silent here, as
 * they do for back's success event.
 */
function emitBackTxError(
  instruction: 'back' | 'unback',
  ctx: BackTxErrorContext,
  fields: BackTxErrorFields,
  submitted: boolean,
): void {
  const name =
    submitted && isAmbiguousLandingError(fields.errorCode) ? 'trade_unknown' : 'trade_error';
  onEvent(name, {
    traceId: ctx.traceId,
    instruction,
    walletType: ctx.activeWallet.type,
    walletAddress: ctx.activeWallet.address,
    latencyMs: Date.now() - ctx.startedAt,
    network: ctx.network,
    marketAddress: ctx.marketPubkey,
    outcomeIndex: ctx.outcomeIndex,
    ...fields,
  });
}

/** A `{ ok: false }` result from the tx engine. */
export function trackBackTxFailure(
  instruction: 'back' | 'unback',
  ctx: BackTxErrorContext,
  error: TxError,
): void {
  emitBackTxError(
    instruction,
    ctx,
    {
      errorCode: error.code,
      stage: error.stage,
      morfiError: error.morfiError ?? 'none',
      recoveryAction: error.recoveryAction.type,
      errorClass: 'TxError',
    },
    true,
  );
}

/**
 * A throw from the build/submit path. Before `submitTx` ran it is a build
 * failure; after, the landing is unknown. Call only when no result came back,
 * so a throw after a `{ ok: false }` (already reported) or after a confirmed
 * submission (post-processing) is not counted as a second or false failure.
 */
export function trackBackTxException(
  instruction: 'back' | 'unback',
  ctx: BackTxErrorContext,
  // ALLOW-UNKNOWN: the caught value from a build/submit try block.
  err: unknown,
  submissionStarted: boolean,
): void {
  emitBackTxError(
    instruction,
    ctx,
    {
      errorCode: submissionStarted ? TxErrorCode.TX_UNKNOWN : TxErrorCode.TX_BUILD_FAILED,
      stage: 'preflight',
      morfiError: 'none',
      recoveryAction: 'prompt_retry',
      errorClass: err instanceof Error ? err.name : typeof err,
    },
    submissionStarted,
  );
}

interface BackMarketDeps {
  readonly submitTx: SubmitTxFn;
  readonly signingDeps: SigningDeps;
  // ALLOW-UNKNOWN: turnkeyDeps is opaque to back-market and passed through to the signer.
  readonly turnkeyDeps: unknown;
  readonly activeWallet: ActiveWallet;
  readonly traceId?: string;
  readonly signal?: AbortSignal;
}

/** Indexed immutable market bindings (base58); supplying them skips the builder's Market RPC read. */
export interface BackingAccounts {
  readonly marketConfig: string;
  readonly quoteMint: string;
  readonly eventSchedule: string | null;
}

interface BackMarketParams {
  /** Market pubkey (base58). The market MUST be in `Backing` state on chain. */
  readonly marketPubkey: string;
  readonly accounts?: BackingAccounts | undefined;
  /**
   * Net stake (curve input) D6 — the amount of share-mint tokens the backer
   * will receive. The contract charges the backing fee ON TOP, so the
   * backer's quote ATA is debited
   * `amountD6 + floor(amountD6 * fee_bps_backing / 10_000)` total.
   * Mirrors the `buy` instruction's `amount_in` semantic.
   */
  readonly amountD6: number;
}

/**
 * Build + submit a `back(amount)` transaction signed by `activeWallet`.
 *
 * Lazy-imports `@morfi/contract-sdk` + `@solana/web3.js` + `bn.js` to
 * keep them out of the initial bundle on routes that don't back.
 *
 * Returns the standard `SubmissionResult` envelope.
 */
export async function backMarketAndSubmit(
  deps: BackMarketDeps,
  params: BackMarketParams,
): Promise<SubmissionResult> {
  const traceId = deps.traceId ?? createTransactionTraceId();
  const [{ buildBack }, { PublicKey }, { default: BN }, { getConnection }] = await Promise.all([
    import('@morfi/contract-sdk'),
    import('@solana/web3.js'),
    import('bn.js'),
    import('@/lib/tx-engine/connection'),
  ]);

  const connection: Connection = getConnection();
  const backer = new PublicKey(deps.activeWallet.address);
  const market = new PublicKey(params.marketPubkey);
  const amount = new BN(params.amountD6);

  const built = await buildBack(connection, {
    backer,
    market,
    amount,
    ...toBuilderAccounts(PublicKey, params.accounts),
  });

  const result = await deps.submitTx(
    {
      instructions: built.instructions,
      signers: built.signerKeys,
      computeUnits: built.computeUnits,
    },
    {
      instructionName: 'back',
      traceId,
      signingDeps: deps.signingDeps,
      activeWallet: deps.activeWallet,
      turnkeyDeps: deps.turnkeyDeps as never,
      metadata: {
        marketAction: 'back',
        marketPubkey: params.marketPubkey,
        amountD6: params.amountD6,
      },
      ...(deps.signal !== undefined ? { signal: deps.signal } : {}),
    },
  );
  if (result.ok) {
    // The back is confirmed on-chain; a cache reconcile problem is not a failed back.
    try {
      await reconcileBackerRewardAfterBacking(params.marketPubkey);
    } catch (err: unknown) {
      logger.warn('proposal_back.reconcile_failed', {
        traceId,
        errorClass: err instanceof Error ? err.name : typeof err,
      });
    }
  }
  return result;
}

interface UnbackMarketParams {
  /** Market pubkey (base58). */
  readonly marketPubkey: string;
  readonly accounts?: BackingAccounts | undefined;
  /** Amount of backer-fee-share tokens to burn (D6). */
  readonly burnAmountD6: string;
}

/**
 * Build + submit an `unback` transaction — burns backer-fee-share tokens
 * to redeem the proportional vault claim. Replaces the old
 * `buildWithdrawProposalStake` flow; the new contract has a single
 * share-burn primitive that takes `burnAmount` (not a USDC amount).
 */
export async function unbackMarketAndSubmit(
  deps: BackMarketDeps,
  params: UnbackMarketParams,
): Promise<SubmissionResult> {
  const [{ buildUnback }, { PublicKey }, { default: BN }, { getConnection }] = await Promise.all([
    import('@morfi/contract-sdk'),
    import('@solana/web3.js'),
    import('bn.js'),
    import('@/lib/tx-engine/connection'),
  ]);

  const connection: Connection = getConnection();
  const backer = new PublicKey(deps.activeWallet.address);
  const market = new PublicKey(params.marketPubkey);
  const burnAmount = new BN(params.burnAmountD6);

  const built = await buildUnback(connection, {
    backer,
    market,
    burnAmount,
    ...toBuilderAccounts(PublicKey, params.accounts),
  });

  return deps.submitTx(
    {
      instructions: built.instructions,
      signers: built.signerKeys,
    },
    {
      instructionName: 'unback',
      traceId: deps.traceId ?? createTransactionTraceId(),
      signingDeps: deps.signingDeps,
      activeWallet: deps.activeWallet,
      turnkeyDeps: deps.turnkeyDeps as never,
      metadata: {
        marketAction: 'unback',
        marketPubkey: params.marketPubkey,
        burnAmountD6: params.burnAmountD6,
      },
      ...(deps.signal !== undefined ? { signal: deps.signal } : {}),
    },
  );
}

function toBuilderAccounts(
  PublicKey: typeof PublicKeyType,
  accounts: BackingAccounts | undefined,
): {
  marketConfig?: PublicKeyType;
  quoteMint?: PublicKeyType;
  eventSchedule?: PublicKeyType | null;
} {
  if (accounts === undefined) return {};
  return {
    marketConfig: new PublicKey(accounts.marketConfig),
    quoteMint: new PublicKey(accounts.quoteMint),
    eventSchedule: accounts.eventSchedule === null ? null : new PublicKey(accounts.eventSchedule),
  };
}
